import { Box, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useContext } from 'react'

// others
import { Entry, EntryStatus } from '../../../generated'
import { FILTER_DATE_FORMAT, NOT_APPLICABLE, SEND_NOTES_COLUMN, SHOW_ENTRY_HEADERS } from '../../constants'
import { AppContext, AuthContext } from '../../context'
import { TimeEntryListingProps } from '../../interfaceTypes/props'

// components
import { CustomPagination } from '../common/CustomPagination'
import { CustomTableCell } from '../common/CustomTableCell'
import { StickyTable } from '../common/StickyTable'
import { TableSkeleton } from '../skeletons/TableSkeleton'
import { EntryMenu } from './EntryMenu'
import { EntrySourceIcon } from './EntrySourceIcon'
import dayjs from 'dayjs'

const EntryListing = ({ state, entryDispatch, loading, refetch, lockCase }: TimeEntryListingProps) => {
  const { allEntries } = state || {}
  const { isAdmin, isParalegal } = useContext(AuthContext)
  const { isExpenseEntry, isNonClientEntry, isLegalFee } = useContext(AppContext)

  const getColumnsForPage = () => {
    const commonHeaders = [...SHOW_ENTRY_HEADERS.common]
    const insertIndex = 3
    if (isExpenseEntry) commonHeaders.splice(insertIndex, 0, ...SHOW_ENTRY_HEADERS.expense)
    else if (isLegalFee) commonHeaders.splice(insertIndex, 0, ...SHOW_ENTRY_HEADERS.legalFee)
    else if (isNonClientEntry) commonHeaders.splice(0, insertIndex, ...SHOW_ENTRY_HEADERS.nonClient)
    else commonHeaders.splice(insertIndex, 0, ...SHOW_ENTRY_HEADERS.duration)
    if (!isNonClientEntry) commonHeaders.splice(-2, 0, ...['Billable', 'Show On Invoice', SEND_NOTES_COLUMN])

    return commonHeaders
  }

  const showColumns = getColumnsForPage()

  const getAmount = (duration: string, rate: number) => {
    const [h = '0', m = '0', s = '0'] = duration.split(':')
    const hours = Number(h) + Number(m) / 60 + Number(s) / 3600
    return (hours * Number(rate)).toFixed(2)
  }

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} />} height="405px">
      <TableHead>
        <TableRow hover>
          {showColumns.map((columnName, index) => {
            return !isAdmin && !isParalegal && columnName === 'User' ? null : (
              <TableCell
                key={index}
                component="th"
                align={columnName === 'Billable' || columnName === 'Show In Invoice' ? 'center' : 'left'}
              >
                {columnName}
              </TableCell>
            )
          })}
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={showColumns.length} />
      ) : (
        <TableBody>
          {allEntries?.map(entry => {
            const {
              id,
              billable,
              case: cases,
              user,
              creator,
              amount,
              duration,
              status,
              category,
              rate,
              showOnBill,
              date,
              createdAt,
              description,
              sendNotesToMatterNotes,
              feeType: feeTypes,
              startTime
            } = entry || {}

            const { feeType } = feeTypes || {}
            const { id: caseId, title, caseNumber, isConsultMatter, consultMatterNumber } = cases ?? {}
            const { firstName, lastName } = user ?? {}
            const creatorName =
              creator?.firstName != null || creator?.lastName != null
                ? [creator?.firstName, creator?.lastName].filter(Boolean).join(' ')
                : null
            const { name: categoryName } = category ?? {}

            return (
              <TableRow key={id} hover>
                {showColumns.includes('Matter ID') && (
                  <CustomTableCell
                    value={`#${isConsultMatter ? consultMatterNumber : caseNumber}`}
                    to={`/matters/matter-detail/${caseId}`}
                    maxWidth="376px"
                  />
                )}
                {showColumns.includes('Matter') && (
                  <CustomTableCell value={`${title ?? ''}`} maxWidth="180px" noWrap={false} />
                )}

                {(isAdmin || isParalegal) && showColumns.includes('User') && (
                  <TableCell align="left" sx={{ maxWidth: 180 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
                    >
                      Work Performed:
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.90rem', display: 'block', marginBottom: '8px' }}
                    >
                      {firstName != null || lastName != null
                        ? [firstName, lastName].filter(Boolean).join(' ')
                        : NOT_APPLICABLE}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}
                    >
                      Work Logged:
                    </Typography>
                    <Typography component="span" variant="body2" sx={{ fontSize: '0.90rem', display: 'block' }}>
                      {creatorName ?? NOT_APPLICABLE}
                    </Typography>
                  </TableCell>
                )}
                {showColumns.includes('Date') && (
                  <TableCell align="left" sx={{ maxWidth: 160 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
                    >
                      Activity Date:
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.90rem', display: 'block', marginBottom: '8px' }}
                    >
                      {date ? dayjs(date).format(FILTER_DATE_FORMAT) : NOT_APPLICABLE}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
                    >
                      Logged Date:
                    </Typography>
                    <Typography component="span" variant="body2" sx={{ fontSize: '0.90rem', display: 'block' }}>
                      {createdAt && !dayjs(createdAt).isBefore(dayjs('2026-02-09'), 'day')
                        ? dayjs(createdAt).format(FILTER_DATE_FORMAT)
                        : NOT_APPLICABLE}
                    </Typography>
                  </TableCell>
                )}
                {showColumns.includes('Time') && (
                  <TableCell align="left">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body1" noWrap>
                        {duration?.toString() || '0'}
                      </Typography>
                      <EntrySourceIcon startTime={startTime} />
                    </Box>
                  </TableCell>
                )}
                {showColumns.includes('Rate') && <CustomTableCell value={`$${rate}`} />}

                {showColumns.includes('Fee Type') && <CustomTableCell value={feeType || NOT_APPLICABLE} />}

                {showColumns.includes('Amount') && (
                  <CustomTableCell
                    value={`$${amount != null ? amount : duration && rate ? getAmount(duration, rate) : 0}`}
                  />
                )}
                {showColumns.includes('Category') && <CustomTableCell value={categoryName || ''} />}

                {showColumns.includes('Description') && (
                  <CustomTableCell noWrap={false} value={description || NOT_APPLICABLE} maxWidth="400px" />
                )}

                {showColumns.includes('Billable') && <CustomTableCell value={!!billable} />}
                {showColumns.includes('Show On Invoice') && <CustomTableCell value={!!showOnBill} maxWidth="500px" />}
                {showColumns.includes(SEND_NOTES_COLUMN) && <CustomTableCell value={!!sendNotesToMatterNotes} />}

                {showColumns.includes('Status') && <CustomTableCell value={status || EntryStatus.Draft} />}

                <TableCell>
                  {status === EntryStatus.Unbilled && (
                    <EntryMenu
                      currentTimeEntry={entry as Entry}
                      entryDispatch={entryDispatch}
                      refetch={refetch}
                      lockCase={lockCase}
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}

export default EntryListing
